var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ReservationStatus } from "../../../models/reservation/reservation.model";
import { getGuestStayStatusInfo } from "../../../utils/color.util";
import { convertLocalDateToDate, startOfDay } from "../../../utils/date.util";
import { ElementChip } from "../../uiElements/base/ElementChip";
import { ElementChipList } from "../../uiElements/base/ElementChipList";
import { AccommodationElement } from "../../uiElements/ResourceElement";
import { getStayStatusDisplay, StayStatusElement } from "../../uiElements/StayStatusElement";
import { CommonThemeColors } from "../../theme";
import { BOARD_ICON_COLOR, getBoardLabel } from "../stayDisplay";
// Longer stays get one bar instead of a segment per night.
var MAX_NIGHT_SEGMENTS = 21;
var NIGHT_TRACK_COLOR = '#E3E6EB';
var daysBetween = function (from, to) { return from && to
    ? Math.round(DateTime.fromJSDate(to).diff(DateTime.fromJSDate(from), 'days').days)
    : 0; };
var formatStayDay = function (date, language) {
    if (!date)
        return '';
    var day = DateTime.fromJSDate(date).setLocale(language);
    return day.toFormat(day.year === DateTime.now().year ? 'ccc dd.MM' : 'ccc dd.MM.yyyy');
};
var formatFromToday = function (date, language) {
    var days = daysBetween(startOfDay(new Date()), date);
    var format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
    return days < 60 ? format.format(days, 'day') : format.format(Math.round(days / 30), 'month');
};
var capitalize = function (text) { return text.charAt(0).toUpperCase() + text.slice(1); };
// Pending stays are enquiries or waiting-list entries, not confirmed arrivals.
var isBooked = function (stay) { return stay.status !== ReservationStatus.PENDING; };
var segmentHoverSx = { py: '6px', my: '-6px', cursor: 'default' };
var NightBar = function (_a) {
    var nights = _a.nights, done = _a.done, tonight = _a.tonight, color = _a.color, nightLabels = _a.nightLabels, barLabel = _a.barLabel;
    if (nights > MAX_NIGHT_SEGMENTS) {
        return (_jsx(Tooltip, __assign({ title: barLabel, arrow: true }, { children: _jsx(Box, __assign({ sx: segmentHoverSx }, { children: _jsx(Box, __assign({ sx: { height: 8, borderRadius: '2px', bgcolor: NIGHT_TRACK_COLOR, overflow: 'hidden' } }, { children: _jsx(Box, { sx: { height: '100%', width: "".concat((done / nights) * 100, "%"), bgcolor: color } }) })) })) })));
    }
    return (_jsx(Box, __assign({ sx: {
            display: 'grid',
            gridTemplateColumns: "repeat(".concat(nights, ", minmax(0, 1fr))"),
            gap: nights > 7 ? '2px' : '4px'
        } }, { children: Array.from({ length: nights }, function (_, night) { return (_jsx(Tooltip, __assign({ title: nightLabels[night], arrow: true }, { children: _jsx(Box, __assign({ sx: segmentHoverSx }, { children: _jsx(Box, { sx: {
                        height: 8,
                        boxSizing: 'border-box',
                        borderRadius: '2px',
                        bgcolor: night < done
                            ? color
                            : night === tonight ? alpha(color, 0.12) : NIGHT_TRACK_COLOR,
                        border: night === tonight ? "1px solid ".concat(color) : undefined
                    } }) })) }), night)); }) })));
};
var StayCard = function (_a) {
    var _b, _c;
    var profile = _a.profile;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var language = i18n.language;
    var statusInfo = getGuestStayStatusInfo(profile);
    var status = statusInfo === null || statusInfo === void 0 ? void 0 : statusInfo.status;
    var upcomingStays = ((_b = profile.upcomingStays) !== null && _b !== void 0 ? _b : [])
        .filter(isBooked)
        .sort(function (a, b) { var _a, _b; return ((_a = a.startDay) !== null && _a !== void 0 ? _a : '').localeCompare((_b = b.startDay) !== null && _b !== void 0 ? _b : ''); });
    // getGuestStayStatusInfo takes the first upcoming stay in the order the API sends; the card shows the nearest.
    var stay = status === 'UPCOMING' ? upcomingStays[0] : statusInfo === null || statusInfo === void 0 ? void 0 : statusInfo.stay;
    var statusColor = (_c = getStayStatusDisplay(status)) === null || _c === void 0 ? void 0 : _c.color;
    if (!stay || !status || !statusColor) {
        return (_jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', gap: '10px', sx: { px: '12px', py: '10px', border: '1px solid', borderColor: 'grey.300', borderRadius: '8px' } }, { children: [_jsx(InfoOutlinedIcon, { sx: { fontSize: 18, color: 'text.secondary' } }), _jsx(Typography, __assign({ sx: { fontSize: 14 }, color: 'text.secondary' }, { children: t('No current or upcoming stay') }))] })));
    }
    var start = convertLocalDateToDate(stay.startDay);
    var end = convertLocalDateToDate(stay.endDay);
    var nights = Math.max(daysBetween(start, end), 1);
    var nightsDone = status === 'CHECKING_OUT'
        ? nights
        : status === 'STAYING'
            ? Math.min(Math.max(daysBetween(start, startOfDay(new Date())), 0), nights - 1)
            : 0;
    var tonight = status === 'CHECKING_IN' || status === 'STAYING' ? nightsDone : undefined;
    var nightLabels = Array.from({ length: nights }, function (_, night) { return "".concat(t('Day {{day}} of {{days}}', { day: night + 1, days: nights }), " \u00B7 ").concat(formatStayDay(start && DateTime.fromJSDate(start).plus({ days: night }).toJSDate(), language)); });
    var barLabel = status === 'UPCOMING'
        ? capitalize(formatFromToday(start, language))
        : nightLabels[Math.min(nightsDone, nights - 1)];
    // A booking that starts the same day runs in parallel (a second room); it is not the next stay.
    var nextStay = upcomingStays.find(function (other) {
        var _a, _b;
        return other !== stay
            && other.reservationId !== stay.reservationId
            && ((_a = other.startDay) !== null && _a !== void 0 ? _a : '') > ((_b = stay.startDay) !== null && _b !== void 0 ? _b : '');
    });
    var nextStart = convertLocalDateToDate(nextStay === null || nextStay === void 0 ? void 0 : nextStay.startDay);
    var nextEnd = convertLocalDateToDate(nextStay === null || nextStay === void 0 ? void 0 : nextStay.endDay);
    var boardLabel = getBoardLabel(stay);
    return (_jsxs(Stack, __assign({ gap: '12px' }, { children: [_jsxs(ElementChipList, { children: [_jsx(StayStatusElement, { stayStatus: status, startDate: stay.startDay, endDate: stay.endDay }), !!stay.roomId && (_jsx(AccommodationElement, { name: stay.roomId })), !!boardLabel && (_jsx(ElementChip, { text: boardLabel, startIcon: _jsx(SellOutlinedIcon, { sx: { fontSize: 15, color: BOARD_ICON_COLOR } }) }))] }), _jsx(NightBar, { nights: nights, done: nightsDone, tonight: tonight, color: statusColor, nightLabels: nightLabels, barLabel: barLabel }), _jsxs(Stack, __assign({ direction: 'row', justifyContent: 'space-between' }, { children: [_jsx(Typography, __assign({ variant: 'body2', color: 'text.secondary' }, { children: formatStayDay(start, language) })), _jsx(Typography, __assign({ variant: 'body2', color: 'text.secondary' }, { children: formatStayDay(end, language) }))] })), nextStay && (_jsxs(Stack, __assign({ direction: 'row', alignItems: 'flex-start', gap: '10px', pt: '4px' }, { children: [_jsx(EventOutlinedIcon, { sx: { fontSize: 18, mt: '1px', color: CommonThemeColors.sys.warning.main } }), _jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(Typography, __assign({ sx: { fontSize: 14, fontWeight: 600 } }, { children: "".concat(status === 'UPCOMING' ? t('Also booked') : t('Next stay'), " \u00B7 ").concat(formatFromToday(nextStart, language)) })), _jsx(Typography, __assign({ sx: { fontSize: 13 }, color: 'text.secondary' }, { children: "".concat(formatStayDay(nextStart, language), " \u2013 ").concat(formatStayDay(nextEnd, language)) }))] }))] })))] })));
};
export default StayCard;
